import { useCallback, useEffect, useRef, useState } from "react";

import { signMediaUrls } from "../api";

/** 1回に頼む件数。Worker 側の上限は120 */
const SIGN_CHUNK = 100;

/**
 * キーごとに署名付きURLを持つ。
 *
 * **一度得たURLを捨てない**のが要点。以前は「表示中の全キー」を1つの問い合わせで
 * 署名していたため、次のページを読み込むたびにキーの並びが変わり、
 *
 * - 取得し直す間、既に出ていたサムネイルが一斉に消える
 * - 署名が変わるのでURLも変わり、ブラウザのキャッシュが効かず全部を再取得する
 * - 3ページ目(180件)で Worker の上限120を超え、そもそも失敗する
 *
 * という状態だった。足りないぶんだけを、上限に収まる大きさに分けて取りに行く。
 */
export function useSignedUrls(keys: string[]): {
  urls: Record<string, string>;
  /** 期限切れなどで表示できなかったときに、全部取り直す */
  refresh: () => void;
} {
  const [urls, setUrls] = useState<Record<string, string>>({});
  // 頼み済みのキー。応答を待っている間に二重で頼まないために持つ
  const requested = useRef(new Set<string>());
  const [generation, setGeneration] = useState(0);

  const refresh = useCallback(() => {
    requested.current = new Set();
    setUrls({});
    setGeneration((current) => current + 1);
  }, []);

  useEffect(() => {
    const missing = keys.filter((key) => !requested.current.has(key));
    if (missing.length === 0) return;
    for (const key of missing) requested.current.add(key);

    let cancelled = false;
    void (async () => {
      for (let start = 0; start < missing.length; start += SIGN_CHUNK) {
        const chunk = missing.slice(start, start + SIGN_CHUNK);
        try {
          const signed = await signMediaUrls(chunk);
          if (cancelled) return;
          setUrls((current) => ({ ...current, ...signed }));
        } catch {
          // 失敗したぶんは頼み済みから外し、次の描画でやり直せるようにする
          for (const key of chunk) requested.current.delete(key);
          return;
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [keys, generation]);

  return { urls, refresh };
}
